import React, { useState } from "react";
import { User, Database, Trash2, X, Send, CircleDashed, Pencil, Coins, Banknote, TrendingUp, Building2, Server, FileText, ArrowUpDown, ArrowUp, ArrowDown, Scale, AlertCircle } from "lucide-react";
import { AccountingEntry, EntryStatus } from "../../../types";
import { StatusBadge } from "../../../components/ui/StatusBadge";
import { formatEUR, formatCurrency, formatOunces } from "../../../lib/format";
import { SourceRowsViewer } from "./SourceRowsViewer";

export function AccountingTable({
  rows,
  toggleRow,
  allSelected,
  toggleAll,
  setRowStatus,
  selectedRows,
  deleteRow,
  onEdit,
  displayMode = 'EUR',
  dateSortOrder = null,
  onDateSortChange,
}: {
  rows: AccountingEntry[];
  toggleRow: (id: string) => void;
  allSelected: boolean;
  toggleAll: () => void;
  setRowStatus: (id: string, status: EntryStatus) => void;
  selectedRows: Record<string, boolean>;
  deleteRow?: (id: string) => void;
  onEdit?: (entry: AccountingEntry) => void;
  displayMode?: 'EUR' | 'ORIGINAL';
  dateSortOrder?: 'asc' | 'desc' | null;
  onDateSortChange?: (order: 'asc' | 'desc' | null) => void;
}) {
  const onToggleAll = () => {
    toggleAll();
  };

  const onToggleRow = (id: string) => {
    toggleRow(id);
  };

  const canEdit = (s: EntryStatus) => s !== "Confermato";
  const [viewingSourceRows, setViewingSourceRows] = useState<number | null>(null);

  return (
    <>
      <SourceRowsViewer
        entryId={viewingSourceRows}
        isOpen={viewingSourceRows !== null}
        onClose={() => setViewingSourceRows(null)}
      />
      <div className="mt-3 space-y-3">
      {/* Header con checkbox per selezionare tutto e ordinamento */}
      <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-50 border border-gray-200">
        <input 
          type="checkbox" 
          checked={allSelected} 
          onChange={onToggleAll}
          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
        <span className="text-sm text-gray-600 font-medium">
          {allSelected ? "Deseleziona tutto" : "Seleziona tutto"}
        </span>
        <span className="text-xs text-gray-500 ml-2">
          ({rows.length} {rows.length === 1 ? "elemento" : "elementi"})
        </span>

        {onDateSortChange && (
          <button
            type="button"
            className={`ml-auto inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-medium transition-colors ${
              dateSortOrder
                ? "border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100"
                : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
            }`}
            onClick={() => {
              if (!dateSortOrder) onDateSortChange('asc');
              else if (dateSortOrder === 'asc') onDateSortChange('desc');
              else onDateSortChange(null);
            }}
            title={
              !dateSortOrder ? "Ordina per data" :
              dateSortOrder === 'asc' ? "Data crescente — clicca per decrescente" :
              "Data decrescente — clicca per rimuovere ordinamento"
            }
          >
            {!dateSortOrder && <ArrowUpDown className="h-3.5 w-3.5" />}
            {dateSortOrder === 'asc' && <ArrowUp className="h-3.5 w-3.5" />}
            {dateSortOrder === 'desc' && <ArrowDown className="h-3.5 w-3.5" />}
            <span>
              Data{dateSortOrder === 'asc' ? ' ↑' : dateSortOrder === 'desc' ? ' ↓' : ''}
            </span>
          </button>
        )}
      </div>

      {/* Lista di card */}
      <div className="space-y-2">
        {rows.map((r) => {
          // Colori di sfondo tenui in base allo stato
          const getStatusBgColor = (status: EntryStatus) => {
            switch (status) {
              case "Registra":
                return "bg-green-50/50"; // Verde tenue per SENT
              case "Ignora":
                return "bg-yellow-50/50"; // Giallo tenue per IGNORE
              case "Confermato":
                return "bg-green-100/50"; // Verde più scuro tenue per CONFIRMED
              default:
                return "bg-white";
            }
          };

          return (
          <div
            key={r.id}
            className={`rounded-xl border shadow-sm transition-all hover:shadow-md ${
              selectedRows[r.id] ? "border-blue-400 ring-2 ring-blue-100" : "border-gray-200"
            } ${getStatusBgColor(r.status)}`}
          >
            <div className="p-4">
              <div className="flex items-start gap-4">
                {/* Checkbox */}
                <div className="pt-1">
                  <input
                    type="checkbox"
                    checked={!!selectedRows[r.id]}
                    onChange={() => onToggleRow(r.id)}
                    disabled={!canEdit(r.status)}
                    className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-30"
                  />
                </div>

                {/* Contenuto principale */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    {/* Info principali */}
                    <div className="flex-1">
                      <div className="flex items-start gap-3 mb-2">
                        <div className="flex flex-col gap-0.5">
                          <div className="flex items-baseline gap-2">
                            <span className="text-lg font-semibold text-gray-900">
                              {new Date(r.date).toLocaleDateString("it-IT", { 
                                day: '2-digit', 
                                month: 'short', 
                                year: 'numeric' 
                              })}
                            </span>
                          </div>
                          {r.tradeDate && (
                            <div className="flex items-baseline gap-1.5">
                              <span className="text-xs text-gray-400">Trade:</span>
                              <span className={`text-xs font-medium ${r.tradeDate === r.date ? 'text-gray-400' : 'text-gray-600'}`}>
                                {new Date(r.tradeDate).toLocaleDateString("it-IT", { 
                                  day: '2-digit', 
                                  month: 'short', 
                                  year: 'numeric' 
                                })}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {r.source === "manual" ? (
                            <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 border border-blue-200" title="Inserita manualmente">
                              <User className="h-3 w-3 text-blue-600" />
                              <span className="text-xs font-medium text-blue-700">Manuale</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-50 border border-gray-200" title="Da file/piattaforma">
                              <Database className="h-3 w-3 text-gray-600" />
                              <span className="text-xs font-medium text-gray-700">File</span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Metadati con icone */}
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-purple-50 border border-purple-200">
                          <TrendingUp className="h-3.5 w-3.5 text-purple-600" />
                          <span className="text-xs font-semibold text-purple-700">{r.operationType}</span>
                        </div>
                        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-50 border border-emerald-200">
                          <Banknote className="h-3.5 w-3.5 text-emerald-600" />
                          <span className="text-xs font-semibold text-emerald-700">{r.currency}</span>
                        </div>
                        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-amber-50 border border-amber-200">
                          <Coins className="h-3.5 w-3.5 text-amber-600" />
                          <span className="text-xs font-semibold text-amber-700">{r.metal}</span>
                        </div>
                        {r.ounces != null && (
                          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-stone-50 border border-stone-200">
                            <Scale className="h-3.5 w-3.5 text-stone-600" />
                            <span className="text-xs font-semibold text-stone-700">{formatOunces(r.ounces)} OZ</span>
                          </div>
                        )}
                      </div>

                      {/* Descrizione */}
                      <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
                        {r.description}
                      </p>
                    </div>

                    {/* Importo e stato */}
                    <div className="flex flex-col items-end gap-2 flex-shrink-0">
                      {r.entryId != null && (
                        <span className="font-mono text-[11px] text-gray-400 select-all">
                          #{r.entryId}
                        </span>
                      )}
                      <div className="text-2xl font-bold text-gray-900 tabular-nums">
                        {displayMode === 'ORIGINAL' && r.currency === 'Dollari' && r.amountOriginal != null
                          ? formatCurrency(r.amountOriginal, 'USD')
                          : formatEUR(r.amount)}
                      </div>
                      {r.currency === 'Dollari' && r.fxRate != null && (() => {
                        const isBdi = r.fxRateSource === 'BDI';
                        const isAhr = r.fxRateSource === 'AHR';
                        const isAnomaly = isBdi && r.fxRateDate != null && r.fxRateDate !== r.date;
                        return (
                          <div className="text-[11px] text-gray-500 tabular-nums text-right">
                            {displayMode === 'ORIGINAL'
                              ? `≈ ${formatEUR(r.amount)}`
                              : `≈ ${formatCurrency(r.amountOriginal || 0, 'USD')}`}
                            <span className={`ml-2 inline-flex items-center gap-1.5 rounded-full px-1.5 py-0.5 border ${isAnomaly ? 'bg-amber-50 border-amber-300' : 'bg-gray-100 border-gray-200'}`}>
                              <span className="text-[10px] text-gray-600">FX {r.fxRate?.toFixed(4)} USD/EUR</span>
                              {isBdi ? (
                                <span
                                  className="inline-flex items-center gap-0.5 cursor-help"
                                  title={isAnomaly
                                    ? `Tasso Banca d'Italia del ${r.fxRateDate} — anomalia: la data del tasso differisce dalla data valuta (${r.date})`
                                    : `Tasso ufficiale Banca d'Italia del ${r.fxRateDate}`}
                                >
                                  <Building2 className={`h-3.5 w-3.5 ${isAnomaly ? 'text-amber-500' : 'text-blue-600'}`} />
                                  {isAnomaly && <AlertCircle className="h-3 w-3 text-amber-500" />}
                                </span>
                              ) : isAhr ? (
                                <span
                                  className="inline-flex items-center cursor-help"
                                  title="Tasso da Ad Hoc Infinity (software gestionale)"
                                >
                                  <Server className="h-3.5 w-3.5 text-amber-600" />
                                </span>
                              ) : (
                                <span
                                  className="inline-flex items-center cursor-help"
                                  title="Tasso inserito manualmente"
                                >
                                  <Pencil className="h-3.5 w-3.5 text-gray-500" />
                                </span>
                              )}
                            </span>
                          </div>
                        );
                      })()}
                      <StatusBadge status={r.status} />
                    </div>
                  </div>

                  {/* Azioni */}
                  <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {r.source === "file" && r.entryId != null && (
                        <button
                          className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors flex items-center gap-1.5"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const id = typeof r.entryId === 'number' ? r.entryId : Number(r.entryId);
                            console.log("[AccountingTable] Click File Source, entryId:", id, "type:", typeof id);
                            if (!isNaN(id) && id > 0) {
                              setViewingSourceRows(id);
                            } else {
                              console.warn("[AccountingTable] entryId non valido:", r.entryId);
                            }
                          }}
                          title="Visualizza righe source"
                          type="button"
                        >
                          <FileText className="h-3.5 w-3.5" />
                          <span>File Source</span>
                        </button>
                      )}
                      {onEdit && (
                        <button
                          className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors flex items-center gap-1.5"
                          onClick={() => onEdit(r)}
                          title="Modifica"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                          <span>Modifica</span>
                        </button>
                      )}
                      {deleteRow && (r.status === "Da processare" || r.status === "Ignora") && (
                        <button
                          className="rounded-lg border border-red-300 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100 hover:border-red-400 transition-colors flex items-center gap-1.5"
                          onClick={() => deleteRow(r.id)}
                          title="Elimina scrittura"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          <span>Elimina</span>
                        </button>
                      )}
                    </div>
                    
                    <div className="flex-1"></div>
                    
                    <div className="flex items-center gap-1.5 flex-wrap justify-end">
                      {/* Bottone "Da processare": visibile solo se NON è PENDING e NON è CONFIRMED */}
                      {r.status !== "Da processare" && r.status !== "Confermato" && (
                        <button
                          className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
                          onClick={() => setRowStatus(r.id, "Da processare")}
                          disabled={!canEdit(r.status)}
                          title="Riporta a Da processare"
                        >
                          <CircleDashed className="h-3.5 w-3.5" />
                          <span className="hidden sm:inline">Da processare</span>
                        </button>
                      )}
                      {/* Bottone "Registra": visibile solo se lo stato è PENDING */}
                      {r.status === "Da processare" && (
                        <button
                          className="rounded-lg border border-blue-300 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
                          onClick={() => setRowStatus(r.id, "Registra")}
                          disabled={!canEdit(r.status)}
                          title="Registra"
                        >
                          <Send className="h-3.5 w-3.5" />
                          <span className="hidden sm:inline">Registra</span>
                        </button>
                      )}
                      {/* Bottone "Ignora": visibile solo se lo stato è PENDING */}
                      {r.status === "Da processare" && (
                        <button
                          className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
                          onClick={() => setRowStatus(r.id, "Ignora")}
                          disabled={!canEdit(r.status)}
                          title="Ignora"
                        >
                          <X className="h-3.5 w-3.5" />
                          <span className="hidden sm:inline">Ignora</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          );
        })}
      </div>

      {rows.length === 0 && (
        <div className="text-center py-12 px-4 rounded-xl border-2 border-dashed border-gray-300 bg-gray-50">
          <Database className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-900 mb-1">Nessuna scrittura trovata</p>
          <p className="text-xs text-gray-500">Prova a cambiare i filtri, crearne una a mano, o caricare dati</p>
        </div>
      )}
      </div>
    </>
  );
}
